/**
 * Speech recognition session state for web dictation.
 *
 * Mirrors `SpeechRecognitionState` from the Multimodal SDK
 * (`crates/multimodal-soniox-client/src/state.rs`) so the web and native
 * surfaces share one lifecycle vocabulary:
 *
 * - `idle` -> `connecting` (session started)
 * - `connecting` -> `active(waitingForSpeech)` (recognizer and capture ready)
 * - `connecting` -> `completed` (stopped before readiness)
 * - `active(waitingForSpeech)` -> `active(receivingSpeech)` (first tokens)
 * - `active(*)` -> `active(*)` (transcript updated)
 * - `active(*)` -> `completed` (stopped)
 * - any non-terminal -> `failed` (error occurred)
 *
 * Terminal states (`completed`, `failed`) latch: a session that has ended is
 * discarded and a new one is created for further recognition. Deviations from
 * the SDK: there is no `finalizingChunk` phase (web has no chunk API) and no
 * `stopping` phase (web closes the recognizer immediately on stop).
 */
/** Shared idle snapshot so `getSnapshot` is referentially stable before any session exists. */
export const IDLE_STATE = { kind: 'idle' };
export const isTerminalState = (state) => state.kind === 'completed' || state.kind === 'failed';
/**
 * Human-readable copy for the hook's legacy `error: string | null` contract.
 * Strings match what the previous implementation emitted so existing toasts
 * and tests are unaffected; new consumers should switch on `error.kind`.
 */
export const describeSpeechRecognitionError = (error) => {
    switch (error.kind) {
        case 'permissionDenied':
            return 'Microphone permission is required for voice input';
        case 'sessionUnavailable':
            return 'Failed to start recording. Unable to start Soniox session';
        case 'transport':
            return 'Transcription connection lost';
        case 'provider':
            return `Soniox error: ${error.message ?? error.code}`;
        case 'speechServicesBlocked':
            return 'Dictation may be blocked on this network';
        case 'webSpeech':
            return `Speech recognition error: ${error.error}`;
        case 'noAudio':
            return describeNoAudio(error.detail);
        case 'startFailed':
            return `Failed to start recording. ${error.message}`;
    }
};
const describeNoAudio = (detail) => {
    switch (detail) {
        case 'trackEnded':
            return 'Microphone was disconnected';
        case 'contextInterrupted':
            return 'Microphone audio was interrupted';
        case 'noFrames':
            return 'No audio from microphone. Check your input device and try again';
    }
};
//# sourceMappingURL=state.js.map